import { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardHeader,
  Table,
  Container,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import { URL } from "../../api";
import axiosInstance from "../../Request";

const LspBalance = () => {
  const [lspBalances, setLspBalances] = useState([]);
  const [fintechLspList, setFintechLspList] = useState([]); // ✅ LSP dropdown list
  const [pageLoader, setPageLoader] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const [updateForm, setUpdateForm] = useState({
    fintechId: "",
    limitAmount: "",
    roi: "",
    actionBy: "",
  });

  const [addForm, setAddForm] = useState({
    fintechId: "",
    limitAmount: "",
    roi: "",
    lspName: "",
  });

  // =========================
  // Fetch LSP Balances
  // =========================
  const fetchLspBalances = async () => {
    setPageLoader(true);
    try {
      const { data } = await axiosInstance.get(URL.LSP_BALANCE_LIST);
      setLspBalances(data);
    } catch {
      toast.error("Failed to fetch LSP balances");
    } finally {
      setPageLoader(false);
    }
  };

  // =========================
  // Fetch Fintech LSP List for Dropdown
  // =========================
  const fetchFintechLspList = async () => {
    try {
      const { data } = await axiosInstance.get(URL.FINTECH_ID_LSP);
      setFintechLspList(data);
    } catch {
      toast.error("Failed to fetch LSP list");
    }
  };

  useEffect(() => {
    fetchLspBalances();
    fetchFintechLspList();
  }, []);

  // =========================
  // Update Modal
  // =========================
  const toggleModal = () => {
    if (!showModal) {
      const userName = localStorage.getItem("user_name") || "";
      setUpdateForm({
        fintechId: "",
        limitAmount: "",
        roi: "",
        actionBy: userName,
      });
    }
    setShowModal(!showModal);
  };

  // =========================
  // Add Wallet Modal
  // =========================
  const toggleAddModal = () => {
    if (!showAddModal) {
      setAddForm({
        fintechId: "",
        limitAmount: "",
        roi: "",
        lspName: "",
      });
    }
    setShowAddModal(!showAddModal);
  };

  // =========================
  // Update LSP Dropdown change
  // =========================
  const handleLspSelect = (e) => {
    const fintechId = e.target.value;
    const selectedLsp = lspBalances.find(
      (lsp) => String(lsp.fintechId) === fintechId
    );

    setUpdateForm((prev) => ({
      ...prev,
      fintechId,
      limitAmount: selectedLsp ? selectedLsp.limitAmount : "",
      roi: selectedLsp ? selectedLsp.roi : "",
    }));
  };

  const handleLimitChange = (e) => {
    setUpdateForm((prev) => ({
      ...prev,
      limitAmount: e.target.value,
    }));
  };

  // =========================
  // Handle Add Wallet LSP dropdown select
  // =========================
  const handleAddLspSelect = (e) => {
    const fintechId = e.target.value;

    const selectedLsp = fintechLspList.find(
      (lsp) => String(lsp.id) === fintechId
    );

    setAddForm((prev) => ({
      ...prev,
      fintechId: fintechId,
      lspName: selectedLsp ? selectedLsp.brandName : "",
    }));
  };

  // =========================
  // Update Wallet Submit
  // =========================
  const handleUpdateSubmit = async () => {
    if (!updateForm.fintechId || !updateForm.limitAmount) {
      toast.error("Please select LSP and enter limit amount");
      return;
    }

    setPageLoader(true);
    try {
      await axiosInstance.post(
        URL.LSP_BALANCE_UPDATE_LIMIT,
        null,
        {
          params: {
            fintechId: Number(updateForm.fintechId),
            limitAmount: Number(updateForm.limitAmount),
            ...(updateForm.roi && { roi: Number(updateForm.roi) }),
          },
        }
      );

      toast.success("Limit amount has been updated successfully");
       // ✅ Auto Calculate Balance after update
      try {
        await axiosInstance.post(URL.LSP_BALANCE_CALCULATE);
        toast.success("Balance recalculated successfully");
      } catch {
        toast.warning("Limit updated but balance recalculation failed");
      }
      fetchLspBalances();
      toggleModal();
    } catch {
      toast.error("Failed to update limit amount");
    } finally {
      setPageLoader(false);
    }
  };

  // =========================
  // Add Wallet Submit
  // =========================
//   const handleAddWalletSubmit = async () => {
//   const { fintechId, limitAmount, roi, lspName } = addForm;

//   if (!fintechId || !limitAmount || !roi || !lspName) {
//     toast.error("Please fill all fields");
//     return;
//   }

//   setPageLoader(true);
//   try {
//     await axiosInstance.post(
//       URL.LSP_BALANCE_CREATE_WALLET,
//       null,
//       {
//         params: {
//           fintechId: Number(fintechId),
//           limitAmount: Number(limitAmount),
//           roi: Number(roi),
//           lspName: lspName,
//         },
//       }
//     );

//     toast.success("LSP wallet added successfully");
//     fetchLspBalances();
//     toggleAddModal();

//   } catch (error) {

//     const backendMessage = error?.response?.data?.message;

//     if (backendMessage?.includes("LSP wallet already exists")) {
//       toast.warning(
//         `LSP wallet with fintechId ${fintechId} already exists`
//       );
//     } else {
//       toast.error("Failed to add LSP wallet");
//     }

//   } finally {
//     setPageLoader(false);
//   }
// };
const handleAddWalletSubmit = async () => {
  const { fintechId, limitAmount, roi, lspName } = addForm;

  if (!fintechId || !limitAmount || !roi || !lspName) {
    toast.error("Please fill all fields");
    return;
  }

  setPageLoader(true);
  try {
    // ✅ Step 1 - Wallet Create
    await axiosInstance.post(
      URL.LSP_BALANCE_CREATE_WALLET,
      null,
      {
        params: {
          fintechId: Number(fintechId),
          limitAmount: Number(limitAmount),
          roi: Number(roi),
          lspName: lspName,
        },
      }
    );
    toast.success("LSP wallet created successfully");

    // ✅ Step 2 - Auto Calculate Balance
    try {
      await axiosInstance.post(URL.LSP_BALANCE_CALCULATE);
      toast.success("Balance calculated successfully");
    } catch {
      toast.warning("Wallet created but balance calculation failed");
    }

    // ✅ Step 3 - Refresh List
    fetchLspBalances();
    toggleAddModal();

  } catch (error) {
    const backendMessage = error?.response?.data?.message;

    if (backendMessage?.includes("LSP wallet already exists")) {
      toast.warning(
        `LSP wallet with fintechId ${fintechId} already exists`
      );
    } else {
      toast.error("Failed to add LSP wallet");
    }
  } finally {
    setPageLoader(false);
  }
};

  // =========================
  // Excel Export
  // =========================
  const handleExportExcel = async () => {
    setPageLoader(true);
    try {
      const res = await axiosInstance.get(
        URL.LSP_BALANCE_EXPORT_EXCEL,
        { responseType: "arraybuffer" }
      );

      const blob = new Blob([res.data], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "LspBalance.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success("Excel downloaded successfully");
    } catch {
      toast.error("Excel export failed");
    } finally {
      setPageLoader(false);
    }
  };

  return (
    <>
      {/* ================= PAGE LOADER ================= */}
      <Modal isOpen={pageLoader} centered backdrop="static">
        <ModalBody className="text-center">
          <Spinner color="primary" />
          <div className="mt-3">Please wait...</div>
        </ModalBody>
      </Modal>

      <div className="header bg-gradient-info pb-6 pt-4 pt-md-7">
        <Container fluid>
          <Row>
            <Col>
              <h1 className="text-white">LSP Balance</h1>
            </Col>
            <Col className="text-right">
              <Button color="warning" className="mr-2" onClick={toggleAddModal}>
                Add Wallet
              </Button>
              <Button color="success" className="mr-2" onClick={handleExportExcel}>
                Export Excel
              </Button>
              <Button color="primary" onClick={toggleModal}>
                Update Wallet
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="mt--7" fluid>
        <Row className="mt-5">
          <Col xl="12">
            <Card className="shadow">
              <CardHeader>
                <h3 className="mb-0">LSP Balance List</h3>
              </CardHeader>

              <Table responsive className="align-items-center table-flush">
                <thead className="thead-light">
                  <tr>
                    <th>Fintech ID</th>
                    <th>LSP Name</th>
                    <th>Limit Amount</th>
                    <th>Balance</th>
                    <th>ROI</th>
                    <th>Created Date</th>
                    <th>Updated Date</th>
                  </tr>
                </thead>
                <tbody>
                  {lspBalances.map((lsp, idx) => (
                    <tr key={idx}>
                      <td>{lsp.fintechId}</td>
                      <td>{lsp.lspName}</td>
                      <td>{lsp.limitAmount}</td>
                      <td>{lsp.balance}</td>
                      <td>{lsp.roi}</td>
                      <td>{new Date(lsp.createdDate).toLocaleDateString()}</td>
                      <td>{new Date(lsp.updateDate).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ================= ADD WALLET MODAL ================= */}
      <Modal isOpen={showAddModal} toggle={toggleAddModal}>
        <ModalHeader toggle={toggleAddModal}>Add LSP Wallet</ModalHeader>
        <ModalBody>
          <Form>
            {/* ✅ Select LSP Dropdown */}
            <FormGroup>
              <Label>Select LSP</Label>
              <Input
                type="select"
                value={addForm.fintechId}
                onChange={handleAddLspSelect}
              >
                <option value="">-- Select LSP --</option>
                {fintechLspList.map((lsp) => (
                  <option key={lsp.id} value={lsp.id}>
                    {lsp.brandName} (Fintech ID: {lsp.id})
                  </option>
                ))}
              </Input>
            </FormGroup>

            <FormGroup>
              <Label>Limit Amount</Label>
              <Input
                value={addForm.limitAmount}
                onChange={(e) =>
                  setAddForm({ ...addForm, limitAmount: e.target.value })
                }
              />
            </FormGroup>

            <FormGroup>
              <Label>ROI</Label>
              <Input
                value={addForm.roi}
                onChange={(e) =>
                  setAddForm({ ...addForm, roi: e.target.value })
                }
              />
            </FormGroup>
          </Form>
        </ModalBody>

        <ModalFooter>
          <Button color="primary" onClick={handleAddWalletSubmit}>
            Create Wallet
          </Button>
          <Button color="secondary" onClick={toggleAddModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* ================= UPDATE WALLET MODAL (UNCHANGED) ================= */}
      <Modal isOpen={showModal} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          Update LSP Limit Amount
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Select LSP</Label>
              <Input type="select" value={updateForm.fintechId} onChange={handleLspSelect}>
                <option value="">-- Select LSP --</option>
                {lspBalances.map((lsp) => (
                  <option key={lsp.fintechId} value={lsp.fintechId}>
                    {lsp.lspName} (Fintech ID: {lsp.fintechId})
                  </option>
                ))}
              </Input>
            </FormGroup>

            <FormGroup>
              <Label>Limit Amount</Label>
              <Input value={updateForm.limitAmount} onChange={handleLimitChange} />
            </FormGroup>

            <FormGroup>
              <Label>ROI (%)</Label>
              <Input
                value={updateForm.roi}
                onChange={(e) =>
                  setUpdateForm((prev) => ({
                    ...prev,
                    roi: e.target.value,
                  }))
                }
              />
            </FormGroup>

            <FormGroup>
              <Label>Action By</Label>
              <Input value={updateForm.actionBy} readOnly />
            </FormGroup>
          </Form>
        </ModalBody>

        <ModalFooter>
          <Button color="primary" onClick={handleUpdateSubmit}>
            Update
          </Button>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default LspBalance;
